/**
 * Utilities
 * @object
 * @constructor
 * @public
 */
const Utils = {
  /**
   * Cookie API
   */
  cookie: {
    /**
     * Get cookie by name
     *
     * @param {string} name The cookie name
     * @returns {string} The cookie value
     */
    get: function(name) {
      var cookieName = encodeURIComponent(name) + '=';
      var cookieStart = document.cookie.indexOf(cookieName);
      var cookieValue = null;
      var cookieEnd;

      if (cookieStart > -1) {
        cookieEnd = document.cookie.indexOf(';', cookieStart);
        if (cookieEnd == -1) {
          cookieEnd = document.cookie.length;
        }
        cookieValue = decodeURIComponent(document.cookie.substring(cookieStart + cookieName.length, cookieEnd));
      }

      return cookieValue;
    },

    /**
     * Set cookie by name
     *
     * @param {string} name The cookie name
     * @param {string} value The cookie value
     * @param {number} expires The cookie expiration date in days
     * @param {string} path The cookie path
     * @param {string} domain The cookie domain
     * @param {boolean} secure The cookie secure flag
     */
    set: function(name, value, expires, path, domain, secure) {
      var cookieText = encodeURIComponent(name) + '=' + encodeURIComponent(value);

      if (expires) {
        var date = new Date();
        date.setTime(date.getTime() + (expires * 24 * 60 * 60 * 1000));
        cookieText += '; expires=' + date.toUTCString();
      }

      if (path) {
        cookieText += '; path=' + path;
      }

      if (domain) {
        cookieText += '; domain=' + domain;
      }

      if (secure) {
        cookieText += '; secure';
      }

      document.cookie = cookieText;
    },

    /**
     * Unset a defined cookie
     *
     * @param {string} name The cookie name
     * @param {string} path The cookie path
     * @param {string} domain The cookie domain
     * @param {boolean} secure The cookie secure flag
     */
    unset: function(name, path, domain, secure) {
      this.set(name, '', new Date(0), path, domain, secure);
    }
  },


  /**
  * String Helper
  */
  string: {
    /**
     * Transforms camelcase-String to dash-seperated-string
     *
     * @param str {String} - camelcased string
     * @returns {String} - string with dashes
     */
    camelCaseToDash: function(str) {
        return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    },

    /**
     * Remove <![CDATA[ ... ]]> from strings
     *
     * @param str {string} - string without CDATA
     * @returns {string} - string with CDATA
     */
    removeCdata: function(str) {
      return str ? str.replace('<![CDATA[', '').replace(']]>', '').trim() : str;
    }
  },

  /**
  * Events Helper
  */
  events: {

    /**
     * Debouncing, executes the function if there was no new event in wait milliseconds
     *
     * Usage:
     * window.addEventListener('resize', Utils.events.debounce(resizeHandler, 250, true));
     *
     * @param {function} fn
     * @param {number} wait
     * @param {boolean} immediate
     * @returns {function} The function callback
     */
    debounce: function(fn, wait, immediate) {
      var timeout;
      return function() {
        var context = this, args = arguments;
        var later = function() {
          timeout = null;
          if (!immediate) fn.apply(context, args);
        };
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) fn.apply(context, args);
      };
    },

    /**
     * In case of a "storm of events", this executes once every threshold
     *
     * Usage:
     * window.addEventListener('resize', Utils.events.throttle(resizeHandler, 250, this));
     *
     * @param {function} fn
     * @param {number} threshold
     * @param {object} scope
     * @returns {function}
     */
    throttle: function(fn, threshold, scope) {
      threshold || (threshold = 250);
      var last, deferTimer;

      return function() {
        var context = scope || this;
        var now = +new Date, args = arguments;

        if (last && now < last + threshold) {
          // Hold on to it
          clearTimeout(deferTimer);
          deferTimer = setTimeout(function() {
            last = now;
            fn.apply(context, args);
          }, threshold);
        } else {
          last = now;
          fn.apply(context, args);
        }
      };
    },

    /**
     * IE11-Support for dispatching events.
     * Optionally use different Event-Name for ie11.
     *
     * Usage:
     * Utils.events.dispatchEventIE11(myNode, 'input', 'change');
     *
     * @param {node} el node
     * @param {string} eventName The default event name
     * @param {string} eventNameIE11 IE11 event name
     * @returns {void}
     */
    dispatchEventIE11: function(el, eventName, eventNameIE11) {
      let isIE11 = !!window.MSInputMethodContext && !!document.documentMode,
        event;
        if (isIE11) {
          event = document.createEvent('Event');
          event.initEvent((eventNameIE11 || eventName), true, true);
        } else {
          event = new Event(eventName);
        }
        el.dispatchEvent(event)
    }
  },

  /**
   * Intersection Helper
   */
  intersect: {
    /**
     * Check if an element intersects with viewport (at least part of it)
     *
     * Usage:
     * Utils.intersect.isElementInViewport(document.querySelector('#some-element'), 30);
     *
     * @param {node} el The element to check
     * @param {number} bottomThreshold Bottom threshold
     * @return {number} delta, added to top-border to increase the threshold
     */
    isElementInViewport: function(el, bottomThreshold) {
      let rect = el.getBoundingClientRect(),
        html = document.documentElement,
        top = bottomThreshold ? rect.top + bottomThreshold : rect.top;

      return (
        rect.bottom >= 0 &&
        rect.right >= 0 &&
        top <= (window.innerHeight || html.clientHeight) &&
        rect.left <= (window.innerWidth || html.clientWidth)
      );
    },

    /**
     * Check if an element is out of the viewport
     *
     * Usage:
     * const elementIsOut = Utils.intersect.isOutOfViewport(document.querySelector('#some-element'));
     * if (elementIsOut.right) { … }
     *
     * @param {node} el The element to check
     * @return {object} A set of booleans for each side of the element
     */
    isOutOfViewport: function(el) {
      const bounding = el.getBoundingClientRect();
      const out = {};
      out.top = bounding.top < 0;
      out.left = bounding.left < 0;
      out.bottom = bounding.bottom > (window.innerHeight || document.documentElement.clientHeight);
      out.right = bounding.right > (window.innerWidth || document.documentElement.clientWidth);
      out.any = out.top || out.left || out.bottom || out.right;
      out.all = out.top && out.left && out.bottom && out.right;
      return out;
    }
  },

  /**
   * XHR Helper
   */
  xhr: {
    /**
     * Asynchronously fetch a document
     *
     * Usage:
     * Utils.xhr.get('https://example.com/example.csv', (data) => {
     *  console.log(data);
     * });
     *
     * @param {string} path A String specifying request path
     * @param {function} callback A function to be executed when the text is retrieved.
     * @returns {function} The callback function containing the response text
     */
    get: function(path, callback) {
      let request = new XMLHttpRequest();
      request.open('GET', path, true);
      request.onload = function() {
        if (this.status >= 200 && this.status < 400) {
          let text = this.responseText;
          callback.call(this, text);
        }
      };
      request.send();
    },

    /**
     * Asynchronously fetch and parse a JSON file
     *
     * Usage:
     * Utils.xhr.getJSON('https://example.com/example.json', (data) => {
     *   console.log(data);
     * });
     *
     * @param {string} path A String specifying the location of the JSON file
     * @param {function} callback A function to be executed when the JSON file is retrieved and parsed
     * @returns {function} The callback function containing the JSON data
     */
    getJSON: function(path, callback) {
      let request = new XMLHttpRequest();
      request.open('GET', path, true);
      request.onload = function() {
        if (this.status >= 200 && this.status < 400) {
          let data = JSON.parse(this.responseText);
          callback.call(this, data);
        }
      };
      request.send();
    },

    /**
     * Asynchronously load a JSONP file
     *
     * Usage:
     * Utils.xhr.getJSONP('https://example.com/example.js', () => {
     *   console.log('File loaded.');
     * });
     *
     * @param {string} path A String specifying the location of the JSONP file
     * @returns {function} Callback
     */
    getJSONP: function(path, callback) {
      let body = document.body;
      let script = document.createElement('script');
      script.src = path;
      script.defer = true;
      body.appendChild(script);
      script.onload = function() {
        body.removeChild(script);
        callback.call(this);
      };
    }
  },
};

export default Utils;
